/**
 * Secure Sync Queue for Offline Operations
 *
 * Queues failed database operations to localStorage and retries on reconnect.
 * Includes security measures to prevent cheating.
 */

import * as Sentry from '@sentry/react';
import { saveCompletedPomodoro } from './userSyncAuth';
import { saveCompletedBreak } from './services/pomodoroService';

const QUEUE_KEY = 'studysaga_sync_queue';
const MAX_QUEUE_SIZE = 10;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface QueuedOperation {
  id: string;
  type: 'pomodoro' | 'break';
  userId: string;
  data: {
    duration_minutes: number;
    xp_earned: number;
    critical_success?: boolean;
    break_type?: 'short' | 'long';
    task_name?: string;
    notes?: string;
  };
  sessionStartedAt: number; // When timer started
  queuedAt: number; // When added to queue
}

/**
 * Generate a simple UUID for deduplication
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Get the current queue from localStorage
 */
export function getQueue(): QueuedOperation[] {
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    if (!stored) return [];

    const queue: QueuedOperation[] = JSON.parse(stored);

    // Filter out expired entries (older than 24 hours)
    const now = Date.now();
    return queue.filter((op) => now - op.queuedAt < MAX_AGE_MS);
  } catch (error) {
    console.error('[SyncQueue] Failed to read queue:', error);
    return [];
  }
}

/**
 * Save the queue to localStorage
 */
function saveQueue(queue: QueuedOperation[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('[SyncQueue] Failed to save queue:', error);
  }
}

/**
 * Queue a failed pomodoro operation for later retry
 */
export function queuePomodoroOperation(
  userId: string,
  data: {
    duration_minutes: number;
    xp_earned: number;
    critical_success?: boolean;
    task_name?: string;
    notes?: string;
  },
  sessionStartedAt: number
): void {
  const queue = getQueue();

  // Limit queue size - remove oldest if at capacity
  while (queue.length >= MAX_QUEUE_SIZE) {
    const removed = queue.shift();
    if (import.meta.env.DEV) console.log('[SyncQueue] Queue full, removed oldest:', removed?.id);
  }

  const operation: QueuedOperation = {
    id: generateId(),
    type: 'pomodoro',
    userId,
    data,
    sessionStartedAt,
    queuedAt: Date.now(),
  };

  queue.push(operation);
  saveQueue(queue);

  if (import.meta.env.DEV) console.log('[SyncQueue] Queued pomodoro operation:', operation.id);
}

/**
 * Queue a failed break operation for later retry
 */
export function queueBreakOperation(
  userId: string,
  data: {
    break_type: 'short' | 'long';
    duration_minutes: number;
    xp_earned: number;
  },
  sessionStartedAt: number
): void {
  const queue = getQueue();

  // Limit queue size
  while (queue.length >= MAX_QUEUE_SIZE) {
    queue.shift();
  }

  const operation: QueuedOperation = {
    id: generateId(),
    type: 'break',
    userId,
    data,
    sessionStartedAt,
    queuedAt: Date.now(),
  };

  queue.push(operation);
  saveQueue(queue);

  if (import.meta.env.DEV) console.log('[SyncQueue] Queued break operation:', operation.id);
}

/**
 * Remove a successfully processed operation from the queue
 */
export function removeFromQueue(operationId: string): void {
  const queue = getQueue();
  const filtered = queue.filter((op) => op.id !== operationId);
  saveQueue(filtered);

  if (import.meta.env.DEV) console.log('[SyncQueue] Removed operation:', operationId);
}

/**
 * Process all pending operations in the queue
 * Called on app startup and when network reconnects
 */
export async function processPendingQueue(): Promise<{ processed: number; failed: number }> {
  const queue = getQueue();

  if (queue.length === 0) {
    return { processed: 0, failed: 0 };
  }

  if (import.meta.env.DEV)
    console.log(`[SyncQueue] Processing ${queue.length} pending operations...`);

  let processed = 0;
  let failed = 0;

  for (const operation of queue) {
    try {
      // Validate operation before processing
      if (!isValidOperation(operation)) {
        if (import.meta.env.DEV)
          console.warn('[SyncQueue] Invalid operation, discarding:', operation.id);
        removeFromQueue(operation.id);
        failed++;
        continue;
      }

      if (operation.type === 'pomodoro') {
        const result = await saveCompletedPomodoro(operation.userId, {
          duration_minutes: operation.data.duration_minutes,
          xp_earned: operation.data.xp_earned,
          critical_success: operation.data.critical_success,
          task_name: operation.data.task_name,
          notes: operation.data.notes,
        });

        // If null was returned, session refresh failed - keep in queue for later
        if (result === null) {
          if (import.meta.env.DEV)
            console.log(`[SyncQueue] Session unavailable, keeping in queue:`, operation.id);
          failed++;
          continue;
        }
      } else if (operation.type === 'break') {
        const result = await saveCompletedBreak(operation.userId, {
          break_type: operation.data.break_type!,
          duration_minutes: operation.data.duration_minutes,
          xp_earned: operation.data.xp_earned,
        });

        // If null was returned, session refresh failed - keep in queue for later
        if (result === null) {
          if (import.meta.env.DEV)
            console.log(
              `[SyncQueue] Session unavailable for break, keeping in queue:`,
              operation.id
            );
          failed++;
          continue;
        }
      }

      // Success - remove from queue
      removeFromQueue(operation.id);
      processed++;

      if (import.meta.env.DEV)
        console.log(`[SyncQueue] ✓ Processed ${operation.type}:`, operation.id);
    } catch (error) {
      if (import.meta.env.DEV)
        console.error(`[SyncQueue] ✗ Failed to process ${operation.type}:`, operation.id, error);
      failed++;

      // Check if it's an auth error
      const isAuthError =
        error instanceof Error &&
        (error.message.includes('Authentication required') ||
          error.message.includes('Unauthorized'));

      // Don't remove from queue - will retry later
      // Report to Sentry, but tag auth errors specifically
      Sentry.captureException(error, {
        tags: {
          type: isAuthError ? 'sync-queue-auth-failed' : 'sync-queue-retry-failed',
          operation: operation.type,
          handled: 'yes', // Mark as handled since we're keeping it in queue
        },
        extra: {
          operationId: operation.id,
          queuedAt: operation.queuedAt,
          userId: operation.userId,
          isAuthError,
        },
      });
    }
  }

  if (import.meta.env.DEV)
    console.log(`[SyncQueue] Completed: ${processed} processed, ${failed} failed`);

  return { processed, failed };
}

/**
 * Validate operation to prevent cheating
 */
function isValidOperation(operation: QueuedOperation): boolean {
  const now = Date.now();

  // Check if operation is too old (> 24 hours)
  if (now - operation.queuedAt > MAX_AGE_MS) {
    if (import.meta.env.DEV) console.warn('[SyncQueue] Operation expired:', operation.id);
    return false;
  }

  // Check if session started before it was queued
  if (operation.sessionStartedAt > operation.queuedAt) {
    if (import.meta.env.DEV) console.warn('[SyncQueue] Invalid timestamps:', operation.id);
    return false;
  }

  // Validate duration is reasonable (1-120 minutes)
  if (operation.data.duration_minutes < 1 || operation.data.duration_minutes > 120) {
    if (import.meta.env.DEV) console.warn('[SyncQueue] Invalid duration:', operation.id);
    return false;
  }

  // Validate XP is reasonable (0-500 for a single session)
  if (operation.data.xp_earned < 0 || operation.data.xp_earned > 500) {
    if (import.meta.env.DEV) console.warn('[SyncQueue] Invalid XP:', operation.id);
    return false;
  }

  return true;
}

/**
 * Clear the entire queue (for testing or reset)
 */
export function clearQueue(): void {
  localStorage.removeItem(QUEUE_KEY);
  if (import.meta.env.DEV) console.log('[SyncQueue] Queue cleared');
}

/**
 * Get queue size for debugging
 */
export function getQueueSize(): number {
  return getQueue().length;
}
